import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TextField,
  Typography,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { Link } from "react-router-dom";
import axios from "../../../utils/axios";

// ---------- Styled Table ----------
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#A7C7E7",
    color: theme.palette.common.black,
    // textAlign: "center",
    padding: 6,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    //  textAlign: "center",
    padding: 6,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: "#becbd8ff",
  },
}));

const FdrDashboard = () => {
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState({
    series: [],
    labels: [],
  });
  const [selectedBankName, setSelectedBankName] = useState("");
  const [bankDetails, setBankDetails] = useState([]);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [bankDetailsError, setBankDetailsError] = useState("");
  const [isBankDialogOpen, setIsBankDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get("/fdr/bank-fdr-status");
      const payload = res.data.payload || [];

      const filtered = payload.filter(
        (item) => item.bankname !== "Grand Total"
      );

      setData(payload);

      setChartData({
        series: filtered.map((item) => Number(item.fdramount)),
        labels: filtered.map((item) => item.bankname),
      });
    } catch (error) {
      console.error(error);
    }
  };

  const formatDate = (date) => date.toISOString().split("T")[0];

  const openBankDetailsDialog = async (bankName) => {
    const maturityDateFrom = formatDate(new Date());
    const maturityDateTo = formatDate(
      new Date(new Date().getTime() + 10 * 24 * 60 * 60 * 1000)
    );

    setSelectedBankName(bankName);
    setBankDetailsLoading(true);
    setBankDetailsError("");
    setIsBankDialogOpen(true);

    try {
      const res = await axios.get("/fdr/list", {
        params: {
          bankName,
          fdrNumber: "",
          branch: "",
          status: "",
          startDateFrom: "",
          startDateTo: "",
          maturityDateFrom,
          maturityDateTo,
        },
      });

      setBankDetails(res.data.payload || []);
    } catch (error) {
      console.error(error);
      setBankDetailsError("Failed to load bank details. Please try again.");
      setBankDetails([]);
    } finally {
      setBankDetailsLoading(false);
    }
  };

  const handleRowClick = (row) => {
    if (row.bankname) {
      openBankDetailsDialog(row.bankname);
    }
  };

  const handleCloseBankDialog = () => {
    setIsBankDialogOpen(false);
    setBankDetails([]);
    setSelectedBankName("");
    setBankDetailsError("");
  };

  const [interestData, setInterestData] = useState([]);
  const [interestChart, setInterestChart] = useState({
    series: [],
    labels: [],
  });

  useEffect(() => {
    fetchInterestData();
  }, []);

const fetchInterestData = async () => {
  try {
    const res = await axios.get(
      "/fdr/get-wise-fdr-interest-status"
    );
    const payload = res.data.payload || [];

    const filtered = payload.filter(
      (item) => item.bankname !== "Grand Total"
    );

    setInterestData(payload);

    setInterestChart({
      series: filtered.map((item) =>
        Number(item.grossinterestamount)
      ),
      labels: filtered.map((item) => item.bankname),
    });
  } catch (err) {
    console.error(err);
  }
};

const interestTotal = interestData.find(
  (item) => item.bankname === "Grand Total"
);



  const grandTotal = data.find(
    (item) => item.bankname === "Grand Total"
  );
  const formatKotiStyle = (num) => {
  if (num === null || num === undefined) return "0.00";

  const fixed = Number(num).toFixed(2);
  const [intPart, decPart] = fixed.split(".");

  // 👉 If less than 1 crore → use standard Indian format
  if (intPart.length <= 8) {
    return Number(num).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // 👉 Koti-style for large numbers
  const lastThree = intPart.slice(-3);
  let rest = intPart.slice(0, -3);

  const groups = [];

  while (rest.length > 3) {
    groups.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }

  // keep leftmost as full (Koti block)
  groups.unshift(rest);

  return `${groups.join(",")},${lastThree}.${decPart}`;
};

  return (
<>
<Box
  display="flex"
  justifyContent="space-between"
  alignItems="center"
  mb={2}
>
  {/* Left side text */}
  <Typography variant="subtitle1" fontWeight={800} sx={{ color: "#0d7ceb" }}>
    Starting Date : 1st July, 2025
  </Typography>

  {/* Right side button */}
  <Button
    variant="contained"
    color="primary"
    component={Link}
    to="/app/account/fdrAccountList"
  >
    FDR A/C List
  </Button>
</Box>
      

    <Grid container spacing={2} alignItems="stretch">
      
      {/* TABLE */}
      <Grid item xs={12} md={7}>
        <Paper sx={{ p: 2, height: 400, display: "flex", flexDirection: "column" }}>
                    <Typography
              variant="h6"
              mb={1}
              align="center"
              fontWeight={700}
              sx={{ color: "#0d7ceb" }}
            >
              FDR Current Status by Bank
            </Typography>

          {/* Scrollable Table */}
          <TableContainer sx={{ flex: 1, overflowY: "auto",scrollbarWidth: "none" }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                 <StyledTableCell sx={{ width: 50 }}>SL</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "left" }}>Bank Name</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>FDR Amount</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>Active FDR</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>Close to Maturity</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>Remarks</StyledTableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {data
                  .filter((row) => row.bankname !== "Grand Total")
                  .map((row, index) => (
                    <StyledTableRow
                      key={index}
                      hover
                      onClick={() => handleRowClick(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <StyledTableCell>{index + 1}</StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "left" }}>
                        {row.bankname}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "right" }}>
                        {formatKotiStyle(row.fdramount)}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "right" }}>
                        {row.activefdrnumber}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "right" }}>
                        {row.closetomaturitynumber}
                      </StyledTableCell>
                      <StyledTableCell sx={{ textAlign: "right" }}>
                        {row.remarks || "-"}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}

                {/* Grand Total */}
                {grandTotal && (
                  <StyledTableRow
                    sx={{
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "#A7C7E7 !important",
                      zIndex: 2, // Ensures it stays above the content
                      fontWeight: "bold",
                    }}
                  >
                    <StyledTableCell><b></b></StyledTableCell>
                    <StyledTableCell><b>Grand Total</b></StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "right" }}>
                      <b>{formatKotiStyle(grandTotal.fdramount)}</b>
                    </StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "right" }}>
                      <b>{(grandTotal.activefdrnumber)}</b>
                    </StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "right" }}>
                      <b>{(grandTotal.closetomaturitynumber)}</b>
                    </StyledTableCell>
                    <StyledTableCell sx={{ textAlign: "right" }}>
                      <b>{grandTotal.remarks || "-"}</b>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* PIE CHART */}
      <Grid item xs={12} md={5}>
        <Paper
          sx={{
            p: 1,
            height: 400,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
                    <Typography variant="h6" mb={8} align="center" fontWeight={700}  sx={{ color: "#0d7ceb" }}>
                FDR Distribution 
                </Typography>

          <Chart
            type="pie"
            height={400}
            series={chartData.series}
            options={{
              labels: chartData.labels,
              legend: {
                position: "right",
              },
              tooltip: {
                y: {
                  formatter: (val) =>
                    Number(val).toLocaleString("en-IN"),
                },
              },
            }}
          />
        </Paper>
      </Grid>

   {/* PIE CHART LEFT */}
  <Grid item xs={12} md={5}>
    <Paper
      sx={{
        p: 2,
        height: 400,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <Typography variant="h6" mb={8} align="center" fontWeight={700} sx={{ color: "#0d7ceb" }}>
        Interest Distribution
      </Typography>

     <Chart
  type="donut"
  height={300}
  series={interestChart.series}
  options={{
    labels: interestChart.labels,

    legend: {
      position: "left",
    },

    plotOptions: {
      pie: {
        donut: {
          size: "45%",
        },
      },
    },

    dataLabels: {
      enabled: true,
    },

    stroke: {
      width: 2,
    },

    fill: {
      type: "gradient",
    },

    tooltip: {
      y: {
        formatter: (val) =>
          Number(val).toLocaleString("en-IN"),
      },
    },

    chart: {
      dropShadow: {
        enabled: true,
        top: 3,
        left: 3,
        blur: 4,
        opacity: 0.2,
      },
    },
  }}
/>
    </Paper>
  </Grid>

  {/* TABLE RIGHT */}
  <Grid item xs={12} md={7}>
    <Paper
      sx={{
        p: 2,
        height: 400,
        display: "flex",
        flexDirection: "column",
      }}
    >

      
      <Box
  display="flex"
  justifyContent="center"
  alignItems="center"
  gap={2}
  mb={1}
>
  <Typography
    variant="h6"
    fontWeight={700}
    sx={{ color: "#0d7ceb" }}
  >
    Interest Status by Bank
  </Typography>

  <TextField
    select
    size="small"
    label="Financial Year"
    defaultValue="2025-2026"
    sx={{
      minWidth: 150,
      "& .MuiInputBase-root": {
        height: 36, // keeps it aligned with h6 visually
      }
    }}
  >
    <MenuItem value="2025-2026">2025-2026</MenuItem>
    <MenuItem value="2026-2027">2026-2027</MenuItem>
  </TextField>
</Box>

      <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
            <StyledTableCell sx={{ width: 50 }}>SL</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "left" }}>Bank Name</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "right" }}>Gross Interest</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "right" }}>Tax</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "right" }}>Excise Duty</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "right" }}>Bank Charge</StyledTableCell>
              <StyledTableCell sx={{ textAlign: "right" }}>Net Interest</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {interestData
              .filter((row) => row.bankname !== "Grand Total")
              .map((row, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell>{index + 1}</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "left" }}>{row.bankname}</StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>
                    {formatKotiStyle(row.grossinterestamount)}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>
                    {formatKotiStyle(row.taxamount)}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>
                    {formatKotiStyle(row.exciseduty)}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>
                    {formatKotiStyle(row.bankcharge)}
                  </StyledTableCell>
                  <StyledTableCell sx={{ textAlign: "right" }}>
                    {formatKotiStyle(row.netinterest)}
                  </StyledTableCell>
                </StyledTableRow>
              ))}

            {/* GRAND TOTAL */}
            {interestTotal && (
              <StyledTableRow
                sx={{
                      position: "sticky",
                      bottom: 0,
                      backgroundColor: "#96bce2 !important",
                      zIndex: 2, // Ensures it stays above the content
                      fontWeight: "bold",
                    }}
              >
                 <StyledTableCell sx={{ textAlign: "left" }}>
                  <b></b>
                </StyledTableCell>
                <StyledTableCell sx={{ textAlign: "left" }}>
                  <b>Grand Total</b>
                </StyledTableCell>
                <StyledTableCell sx={{ textAlign: "right" }}>
                  <b>{formatKotiStyle(interestTotal.grossinterestamount)}</b>
                </StyledTableCell>
                <StyledTableCell sx={{ textAlign: "right" }}>
                  <b>{formatKotiStyle(interestTotal.taxamount)}</b>
                </StyledTableCell>
                <StyledTableCell sx={{ textAlign: "right" }}  >
                  <b>{formatKotiStyle(interestTotal.exciseduty)}</b>
                </StyledTableCell>
                <StyledTableCell sx={{ textAlign: "right" }}>
                  <b>{formatKotiStyle(interestTotal.bankcharge)}</b>
                </StyledTableCell>
                <StyledTableCell sx={{ textAlign: "right" }}>
                  <b>{formatKotiStyle(interestTotal.netinterest)}</b>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  </Grid>

   
    </Grid>
      <Dialog
        open={isBankDialogOpen}
        onClose={handleCloseBankDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>FDR Details for {selectedBankName}</DialogTitle>
        <DialogContent dividers>
          {bankDetailsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : bankDetailsError ? (
            <Typography color="error">{bankDetailsError}</Typography>
          ) : bankDetails.length === 0 ? (
            <Typography>
              No FDR records found for this bank within the selected maturity date range.
            </Typography>
          ) : (
            <>
              <Typography variant="subtitle2" mb={2}>
                Showing {bankDetails.length} record{bankDetails.length > 1 ? "s" : ""}
              </Typography>

              {bankDetails.length > 1 && (
                <>
                  <Divider sx={{ my: 3 }} />
           
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {/* Add serial number column */}
                         <StyledTableCell>SL</StyledTableCell>
                        <StyledTableCell>FDR Number</StyledTableCell>
                        <StyledTableCell>Start Date</StyledTableCell>
                        <StyledTableCell>Maturity Date</StyledTableCell>
                        <StyledTableCell>Remaining Days</StyledTableCell>
                        <StyledTableCell align="right">Principal</StyledTableCell>
                        <StyledTableCell align="right">Status</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bankDetails.map((item, idx) => (
                        <StyledTableRow key={idx}>
                          <StyledTableCell>{idx + 1}</StyledTableCell>
                          <StyledTableCell>{item.fdrNumber}</StyledTableCell>
                          <StyledTableCell>{item.startDate}</StyledTableCell>
                          <StyledTableCell>{item.maturityDate}</StyledTableCell>
                          <StyledTableCell>{item.remainingdays ?? "-"}</StyledTableCell>
                          <StyledTableCell align="right">
                            {formatKotiStyle(item.principalAmount)}
                          </StyledTableCell>
                          <StyledTableCell align="right">{item.status}</StyledTableCell>
                        </StyledTableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBankDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FdrDashboard;